import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {VideoItem} from '@PosterWhiteboard/items/video-item/video-item.class';
import type {ImageSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/image-slide-item.class';
import type {VideoSlideItem} from '@PosterWhiteboard/items/slideshow-item/slide-items/video-slide-item.class';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';

/** The media type an AI pick generates — each generator panel produces exactly one. */
export enum MediaSourceGroup {
  IMAGE = 'image',
  VIDEO = 'video',
}

/** Second level: where the media comes from. */
export enum MediaSourceKind {
  UPLOADS = 'uploads',
  STOCK = 'stock',
  AI = 'ai',
}

/**
 * What picking a leaf should DO. The menu shape is identical across surfaces but the mutation is
 * not — appending a background to the timeline sequence, setting the page background, and swapping
 * the selected media are three different writes, so intent travels with every request.
 */
export enum MediaSourceIntent {
  APPEND_BACKGROUND = 'append-background',
  SET_BACKGROUND = 'set-background',
  REPLACE_BACKGROUND = 'replace-background',
  REPLACE_ITEM = 'replace-item',
}

/**
 * One selectable option: a source, usually crossed with a group. `group: null` means the pick spans
 * media types — the top-level Uploads shortcut, whose modal lists image AND video backgrounds, and
 * the flat set-background menu's Explore media row, whose stock panel offers photos AND videos.
 * AI always names a media type: each generator panel produces exactly one.
 */
export interface MediaSourceLeaf {
  group: MediaSourceGroup | null;
  kind: MediaSourceKind;
}

/** What is being replaced. Decides the replace menu's AI entries and the stock panel's tab order. */
export enum ReplaceMediaKind {
  IMAGE = 'image',
  VIDEO = 'video',
  STICKER = 'sticker',
  ICON = 'icon',
  AUDIO = 'audio',
}

export type ReplaceAiPanelKey = 'ai-images' | 'ai-videos' | 'ai-stickers' | 'ai-voice';

/**
 * A pick from the flat replace menu. Unlike MediaSourceLeaf there is no media-type group — the
 * item being replaced names its own type, and the AI rows carry the exact panel they open.
 */
export type ReplaceSourceSelection = {kind: MediaSourceKind.UPLOADS} | {kind: MediaSourceKind.STOCK} | {kind: MediaSourceKind.AI; aiPanel: ReplaceAiPanelKey};

/** The per-surface class names the roomier thumbnail rows need; each surface owns its own SCSS module. */
export interface ReplaceRowThumbnailStyling {
  thumbnailClasses: string;
  /** Applied to non-AI icons only; AI icons always take the AI tint. */
  iconClasses: string;
}

/** One flat replace-menu row, ready for a surface to map onto its own row style. */
export interface ReplaceSourceOption {
  id: string;
  icon: string;
  text: string;
  /** Sub-text for the roomier mobile/popup rows; compact dropdown rows ignore it. */
  subText: string;
  isAi: boolean;
  onSelect: () => void;
}

export type ReplaceableMediaItem = ImageItem | ImageSlideItem | VideoItem | VideoSlideItem;

/**
 * A resolved pick, ready for the dispatcher. Item replacement is the only intent that needs a
 * target and a write-back callback, so it carries them and the background intents cannot.
 */
export type MediaSourceRequest =
  | {
      intent: MediaSourceIntent.APPEND_BACKGROUND | MediaSourceIntent.SET_BACKGROUND;
      leaf: MediaSourceLeaf;
    }
  | {
      intent: MediaSourceIntent.REPLACE_BACKGROUND;
      selection: ReplaceSourceSelection;
    }
  | {
      intent: MediaSourceIntent.REPLACE_ITEM;
      selection: ReplaceSourceSelection;
      item: ReplaceableMediaItem;
      onReplace: (itemData: ItemData) => void;
    };

/** A leaf as the renderers need it: display data plus the click that dispatches it. */
export interface MediaSourceOption {
  leaf: MediaSourceLeaf;
  id: string;
  text: string;
  icon: string;
  /** Sub-text for the roomier flat rows; the compact flyout rows ignore it. */
  subText: string;
  isAi: boolean;
  onSelect: () => void;
}

/** How a source row is drawn. Each surface keeps whatever row style its other options already use. */
export enum MediaSourceRowStyle {
  /** Icon + label. Matches the right panel and timeline popups. */
  COMPACT = 'compact',
  /** Thumbnail tile + label + sub-text. Matches the add-item bar's roomier rows. */
  DETAILED = 'detailed',
}

export interface BuildReplaceSourceOptionsParams {
  idPrefix: string;
  onSelect: (selection: ReplaceSourceSelection) => void;
}

/** The design-system class that tints AI affordances. */
export const AI_CONTENT_CLASS = 'content-ai';

export interface MediaSourceMenuProps {
  /** The flat source rows, in render order (see buildFlatBackgroundSourceOptions). */
  leadingOptions?: MediaSourceOption[];
  /** Classes for each row. Defaults to a grid-2 bottom margin; pass spacing-m-b-0 for a flush menu. */
  leadingOptionClassName?: string;
  /** Defaults to COMPACT. */
  rowStyle?: MediaSourceRowStyle;
}
